import { ref, computed, watch } from "vue";
import {
  ruleOperators,
  ruleActionTypes,
  ruleConditionTypes,
  ruleTriggerTypes,
  ruleEvaluationTypes,
  fieldTypes,
} from "@/src/constants/formConstants.js";

export function useFormRules(rules, formData, formFields) {
  const fieldVisibility = ref({});
  const fieldReadonly = ref({});
  const fieldRequired = ref({});
  const fieldValues = ref({});

  const isSection = (fieldKey) => fieldKey.startsWith('section-');
  
  const isOnLoadRule = (rule) => rule.conditions?.some(
    c => c.type === ruleConditionTypes.FORM && c.operator === ruleTriggerTypes.ON_LOAD
  );

  const evaluateCondition = (condition, formData, triggerType = ruleTriggerTypes.ON_LOAD) => {
    if (condition.type === ruleConditionTypes.FORM) {
      return condition.operator === triggerType;
    }

    if (condition.type === ruleConditionTypes.PROPERTY) {
      const fieldKey = condition.field;
      const fieldValue = formData[fieldKey];
      const operator = condition.operator;
      const conditionValue = condition.value;

      switch (operator) {
        case ruleOperators.EQUALS:
          if (typeof fieldValue === 'string' && typeof conditionValue === 'string') {
            return fieldValue.toLowerCase() === conditionValue.toLowerCase();
          }
          return fieldValue === conditionValue;
        case ruleOperators.NOT_EQUALS:
          if (typeof fieldValue === 'string' && typeof conditionValue === 'string') {
            return fieldValue.toLowerCase() !== conditionValue.toLowerCase();
          }
          return fieldValue !== conditionValue;
        case ruleOperators.CONTAINS:
          if (Array.isArray(conditionValue)) {
            return conditionValue.some((val) =>
              String(fieldValue || "").includes(val),
            );
          }
          return String(fieldValue || "").includes(
            String(conditionValue || ""),
          );
        case ruleOperators.DOES_NOT_CONTAIN:
          if (Array.isArray(conditionValue)) {
            return !conditionValue.some((val) =>
              String(fieldValue || "").includes(val),
            );
          }
          return !String(fieldValue || "").includes(
            String(conditionValue || ""),
          );
        case ruleOperators.STARTS_WITH:
          if (Array.isArray(conditionValue)) {
            return conditionValue.some((val) =>
              String(fieldValue || "").startsWith(val),
            );
          }
          return String(fieldValue || "").startsWith(
            String(conditionValue || ""),
          );
        case ruleOperators.ENDS_WITH:
          if (Array.isArray(conditionValue)) {
            return conditionValue.some((val) =>
              String(fieldValue || "").endsWith(val),
            );
          }
          return String(fieldValue || "").endsWith(
            String(conditionValue || ""),
          );
        case ruleOperators.IS_EMPTY:
          return (
            !fieldValue ||
            fieldValue === "" ||
            (Array.isArray(fieldValue) && fieldValue.length === 0)
          );
        case ruleOperators.IS_NOT_EMPTY:
          return (
            fieldValue &&
            fieldValue !== "" &&
            !(Array.isArray(fieldValue) && fieldValue.length === 0)
          );
        case ruleOperators.GREATER_THAN:
          return Number(fieldValue) > Number(conditionValue);
        case ruleOperators.LESS_THAN:
          return Number(fieldValue) < Number(conditionValue);
        case ruleOperators.GREATER_THAN_OR_EQUAL:
          return Number(fieldValue) >= Number(conditionValue);
        case ruleOperators.LESS_THAN_OR_EQUAL:
          return Number(fieldValue) <= Number(conditionValue);
        case ruleOperators.BETWEEN:
          const numValue = Number(fieldValue);
          return (
            numValue >= Number(conditionValue.min) &&
            numValue <= Number(conditionValue.max)
          );
        case ruleOperators.BEFORE:
          return new Date(fieldValue) < new Date(conditionValue);
        case ruleOperators.AFTER:
          return new Date(fieldValue) > new Date(conditionValue);
        case ruleOperators.CONTAINS_ANY:
          if (Array.isArray(fieldValue) && Array.isArray(conditionValue)) {
            return conditionValue.some((val) => fieldValue.includes(val));
          }
          return false;
        case ruleOperators.CONTAINS_ALL:
          if (Array.isArray(fieldValue) && Array.isArray(conditionValue)) {
            return conditionValue.every((val) => fieldValue.includes(val));
          }
          return false;
        case ruleOperators.IS:
          if (typeof fieldValue === 'string' && typeof conditionValue === 'string') {
            return fieldValue.toLowerCase() === conditionValue.toLowerCase();
          }
          return fieldValue === conditionValue;
        default:
          return false;
      }
    }

    return false;
  };

  const evaluateRule = (rule, formData, triggerType = ruleTriggerTypes.ON_LOAD) => {
    if (!rule.conditions || rule.conditions.length === 0) return false;

    const conditionResults = rule.conditions.map((condition) =>
      evaluateCondition(condition, formData, triggerType),
    );

    const conditionType = rule.conditionType || rule.condition_type || ruleEvaluationTypes.ANY;

    if (conditionType === ruleEvaluationTypes.ALL) {
      return conditionResults.every((result) => result === true);
    } else {
      return conditionResults.some((result) => result === true);
    }
  };

  const applyRuleActions = (rule, formData) => {
    if (!rule.actions) return;

    const actionHandlers = {
      [ruleActionTypes.SHOW]: (fieldKey) => fieldVisibility.value[fieldKey] = true,
      [ruleActionTypes.HIDE]: (fieldKey) => fieldVisibility.value[fieldKey] = false,
      [ruleActionTypes.MAKE_READONLY]: (fieldKey) => fieldReadonly.value[fieldKey] = true,
      [ruleActionTypes.MAKE_MANDATORY]: (fieldKey) => fieldRequired.value[fieldKey] = true,
    };

    rule.actions.forEach((action) => {
      const handler = actionHandlers[action.type];
      if (handler && action.fields && Array.isArray(action.fields)) {
        action.fields.forEach(handler);
      }
    });
  };

  const reverseRuleActions = (rule) => {
    if (!rule.actions) return;

    const reverseHandlers = {
      [ruleActionTypes.MAKE_READONLY]: (fieldKey) => fieldReadonly.value[fieldKey] = false,
      [ruleActionTypes.MAKE_MANDATORY]: (fieldKey) => fieldRequired.value[fieldKey] = false,
    };

    rule.actions.forEach((action) => {
      const handler = reverseHandlers[action.type];
      if (handler && action.fields && Array.isArray(action.fields)) {
        action.fields.forEach(handler);
      }
    });
  };

  const initializeFieldStates = () => {
    const initFields = (fields) => {
      fields.forEach((field) => {
        if (field.type === fieldTypes.SECTION) {
          const sectionKey = field.id;
          fieldVisibility.value[sectionKey] = true;

          if (field.children) {
            initFields(field.children);
          }
        } else if (field.type !== fieldTypes.TEXT_DISPLAY && field.type !== fieldTypes.IMAGE) {
          fieldVisibility.value[field.id] = true;
          fieldReadonly.value[field.id] = false;
          fieldRequired.value[field.id] = field.required || false;
        }
      });
    };

    if (formFields.value && formFields.value.length > 0) {
      initFields(formFields.value);
    }
  };

  const getAllFieldIds = () => {
    const fieldIds = new Set();
    
    const collectFieldIds = (fields) => {
      fields.forEach((field) => {
        if (field.type === fieldTypes.SECTION) {
          if (field.children) {
            collectFieldIds(field.children);
          }
        } else if (field.type !== fieldTypes.TEXT_DISPLAY && field.type !== fieldTypes.IMAGE) {
          fieldIds.add(field.id);
        }
      });
    };
    
    if (formFields.value && formFields.value.length > 0) {
      collectFieldIds(formFields.value);
    }
    
    return fieldIds;
  };

  const calculateRuleState = (rule, allFieldIds) => {
    const ruleVisibleFields = new Set(allFieldIds);
    const ruleMandatoryFields = new Set();
    const ruleReadonlyFields = new Set();
    
    rule.actions?.forEach((action) => {
      if (action.type === ruleActionTypes.HIDE && action.fields) {
        action.fields.forEach(f => ruleVisibleFields.delete(f));
      } else if (action.type === ruleActionTypes.SHOW && action.fields) {
        action.fields.forEach(f => ruleVisibleFields.add(f));
      } else if (action.type === ruleActionTypes.MAKE_MANDATORY && action.fields) {
        action.fields.forEach(f => {
          ruleMandatoryFields.add(f);
          ruleVisibleFields.add(f);
        });
      } else if (action.type === ruleActionTypes.MAKE_READONLY && action.fields) {
        action.fields.forEach(f => {
          ruleReadonlyFields.add(f);
          ruleVisibleFields.add(f);
        });
      }
    });
    
    return {
      visibleFields: ruleVisibleFields,
      mandatoryFields: ruleMandatoryFields,
      readonlyFields: ruleReadonlyFields,
    };
  };

  const collectFieldsFromActions = (actions, targetSets) => {
    const { showFields, hideFields, readonlyFields, mandatoryFields } = targetSets;
    
    actions?.forEach((action) => {
      if (action.type === ruleActionTypes.SHOW && action.fields) {
        action.fields.forEach(f => showFields.add(f));
      } else if (action.type === ruleActionTypes.HIDE && action.fields) {
        action.fields.forEach(f => hideFields.add(f));
      } else if (action.type === ruleActionTypes.MAKE_READONLY && action.fields) {
        action.fields.forEach(f => readonlyFields.add(f));
      } else if (action.type === ruleActionTypes.MAKE_MANDATORY && action.fields) {
        action.fields.forEach(f => mandatoryFields.add(f));
      }
    });
  };

  const evaluateAndCollectRules = (dataToEvaluate, triggerType) => {
    const onLoadShowFields = new Set();
    const onLoadHideFields = new Set();
    const onLoadReadonlyFields = new Set();
    const onLoadMandatoryFields = new Set();
    const propertyRuleStates = [];
    let hasOnLoadRule = false;
    
    const allFieldIds = getAllFieldIds();

    rules.value.rules.forEach((rule, ruleIndex) => {
      const ruleMatches = evaluateRule(rule, dataToEvaluate, triggerType);

      if (ruleMatches) {
        const ruleIsOnLoad = isOnLoadRule(rule);
        
        if (ruleIsOnLoad) {
          hasOnLoadRule = true;
          collectFieldsFromActions(rule.actions, {
            showFields: onLoadShowFields,
            hideFields: onLoadHideFields,
            readonlyFields: onLoadReadonlyFields,
            mandatoryFields: onLoadMandatoryFields,
          });
        } else {
          const ruleState = calculateRuleState(rule, allFieldIds);
          propertyRuleStates.push(ruleState);
        }
      }
    });

    return {
      hasOnLoadRule,
      onLoadShowFields,
      onLoadHideFields,
      onLoadReadonlyFields,
      onLoadMandatoryFields,
      propertyRuleStates,
    };
  };

  const applyOnLoadVisibility = (onLoadShowFields, onLoadHideFields) => {
    const hasExplicitShowFields = onLoadShowFields.size > 0;
    
    Object.keys(fieldVisibility.value).forEach(fieldKey => {
      if (isSection(fieldKey)) return;
      
      if (onLoadHideFields.has(fieldKey)) {
        fieldVisibility.value[fieldKey] = false;
      } else if (hasExplicitShowFields) {
        fieldVisibility.value[fieldKey] = onLoadShowFields.has(fieldKey);
      } else {
        fieldVisibility.value[fieldKey] = true;
      }
    });
  };

  const getFieldOriginalRequired = (fieldKey) => {
    const findField = (fields) => {
      for (const field of fields) {
        if (field.id === fieldKey) {
          return field.required || false;
        }
        if (field.type === fieldTypes.SECTION && field.children) {
          const found = findField(field.children);
          if (found !== null) return found;
        }
      }
      return null;
    };
    
    if (formFields.value && formFields.value.length > 0) {
      const originalRequired = findField(formFields.value);
      return originalRequired !== null ? originalRequired : false;
    }
    return false;
  };

  const applyPropertyRules = (propertyRuleStates) => {
    if (propertyRuleStates.length === 0) {
      Object.keys(fieldVisibility.value).forEach(fieldKey => {
        if (isSection(fieldKey)) return;
        
        fieldVisibility.value[fieldKey] = true;
        fieldReadonly.value[fieldKey] = false;
        fieldRequired.value[fieldKey] = getFieldOriginalRequired(fieldKey);
      });
      return;
    }

    const unionVisibleFields = new Set();
    const unionMandatoryFields = new Set();
    const unionReadonlyFields = new Set();
    
    propertyRuleStates.forEach(ruleState => {
      ruleState.visibleFields.forEach(fieldId => unionVisibleFields.add(fieldId));
      ruleState.mandatoryFields.forEach(fieldId => unionMandatoryFields.add(fieldId));
      ruleState.readonlyFields.forEach(fieldId => unionReadonlyFields.add(fieldId));
    });
    
    Object.keys(fieldVisibility.value).forEach(fieldKey => {
      if (isSection(fieldKey)) return;
      
      fieldVisibility.value[fieldKey] = unionVisibleFields.has(fieldKey);
      fieldReadonly.value[fieldKey] = unionReadonlyFields.has(fieldKey);
      fieldRequired.value[fieldKey] = unionMandatoryFields.has(fieldKey) || getFieldOriginalRequired(fieldKey);
    });
  };

  const executeRules = (triggerType = ruleTriggerTypes.ON_LOAD, currentFormData = null) => {
    const dataToEvaluate = currentFormData || formData.value;

    if (!rules.value || !rules.value.rules) return;

    const {
      hasOnLoadRule,
      onLoadShowFields,
      onLoadHideFields,
      onLoadReadonlyFields,
      onLoadMandatoryFields,
      propertyRuleStates,
    } = evaluateAndCollectRules(dataToEvaluate, triggerType);

    if (hasOnLoadRule) {
      applyOnLoadVisibility(onLoadShowFields, onLoadHideFields);
    }

    applyPropertyRules(propertyRuleStates);
  };

  watch(
    () => formData.value,
    (newData) => {
      executeRules(ruleTriggerTypes.ON_LOAD, newData);
    },
    { deep: true },
  );

  const isFieldVisible = (fieldKey) => {
    return fieldVisibility.value[fieldKey] !== false;
  };

  const isFieldReadonly = (fieldKey) => {
    return fieldReadonly.value[fieldKey] === true;
  };

  const isFieldRequired = (fieldKey) => {
    return fieldRequired.value[fieldKey] === true;
  };

  const getFieldValue = (fieldKey) => {
    return fieldValues.value[fieldKey];
  };

  return {
    fieldVisibility,
    fieldReadonly,
    fieldRequired,
    fieldValues,
    initializeFieldStates,
    executeRules,
    isFieldVisible,
    isFieldReadonly,
    isFieldRequired,
    getFieldValue,
  };
}
